import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useProgress } from './hooks/useProgress';
import { Toaster } from 'sonner';

// Pages
import WelcomePage from './pages/WelcomePage';
import OnboardingPage from './pages/OnboardingPage';
import HomePage from './pages/HomePage';
import LessonPage from './pages/LessonPage';
import LessonCompletePage from './pages/LessonCompletePage';
import ProgressPage from './pages/ProgressPage';

function App() {
  const { 
    progress, 
    isLoaded, 
    completeOnboarding, 
    completeLesson,
    isLessonUnlocked,
    isLessonCompleted,
    getLessonAccuracy,
    resetProgress
  } = useProgress();

  // Show loading state while progress is being loaded from localStorage
  if (!isLoaded) {
    return (
      <div className="app-container min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const handleOnboardingComplete = (parentName, childName, childAge) => {
    resetProgress(); // Reset any existing progress
    completeOnboarding(parentName, childName, childAge);
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Welcome/Landing page */}
          <Route 
            path="/" 
            element={<WelcomePage hasProgress={progress.isOnboarded} />} 
          />

          {/* Onboarding */}
          <Route 
            path="/onboarding" 
            element={<OnboardingPage onComplete={handleOnboardingComplete} />} 
          />

          {/* Home - requires onboarding */}
          <Route 
            path="/home" 
            element={
              progress.isOnboarded ? (
                <HomePage 
                  progress={progress}
                  isLessonUnlocked={isLessonUnlocked}
                  isLessonCompleted={isLessonCompleted}
                  getLessonAccuracy={getLessonAccuracy}
                />
              ) : (
                <Navigate to="/onboarding" replace />
              )
            } 
          />

          {/* Lesson */}
          <Route 
            path="/lesson/:lessonId" 
            element={
              progress.isOnboarded ? (
                <LessonPage onComplete={completeLesson} />
              ) : (
                <Navigate to="/onboarding" replace />
              )
            } 
          />

          {/* Lesson Complete */}
          <Route 
            path="/lesson/:lessonId/complete" 
            element={
              progress.isOnboarded ? (
                <LessonCompletePage />
              ) : (
                <Navigate to="/onboarding" replace />
              )
            } 
          />

          {/* Progress/All Lessons */}
          <Route 
            path="/progress" 
            element={
              progress.isOnboarded ? (
                <ProgressPage 
                  progress={progress}
                  isLessonUnlocked={isLessonUnlocked}
                  isLessonCompleted={isLessonCompleted}
                  getLessonAccuracy={getLessonAccuracy}
                />
              ) : (
                <Navigate to="/onboarding" replace />
              )
            } 
          />

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
